import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Calculator, AlertTriangle, Settings2, BarChart3 } from 'lucide-react';

// Import calculation engines
import {
  getAnnualContribution,
  simulateAccumulation,
  simulateDrawdown,
  calculateRetirementMetrics,
  runRetirementMonteCarlo,
  compareWithdrawalStrategies,
  estimateStatePension
} from '../utils/retirement/retirementEngine';

import { generateRetirementInsights, generateActionItems } from '../utils/retirement/retirementInsights';
import { getCurrencySymbol } from '../countryConfig';
import { getInvestmentMarketValue } from '../utils/netWorthCalculations';
import { usePortfolioPrices } from '../hooks/usePricing';

// Import scenario management
import { saveScenario } from '../utils/retirement/scenarioManager';
import { loadRetirementInputs, debouncedSaveInputs, getSavedInputsInfo } from '../utils/retirement/inputPersistence';

// Import new UI components
import QuickStatsPanel from './retirement/shared/QuickStatsPanel';
import FloatingActionBar from './retirement/shared/FloatingActionBar';
import RetirementInputTabs from './retirement/inputs/RetirementInputTabs';
import AnalysisTabs from './retirement/analysis/AnalysisTabs';

/**
 * Enhanced Retirement Planner Component - Redesigned UI
 *
 * NEW ARCHITECTURE:
 * - Main tabs: Configure | Analysis
 * - Configure sub-tabs: Basics | Income Sources | Strategy | Advanced
 * - Analysis sub-tabs: Overview | Accumulation | Drawdown | Monte Carlo | Strategies | Scenarios
 * - Sticky QuickStats at top
 * - Sticky FloatingActionBar at bottom
 * - Auto-save inputs to localStorage
 * - UK/US localization preserved
 */
export default function RetirementPlanner({ accounts = [], investments = [], country = 'US', isPremium = false }) {
  // Get currency symbol based on country
  const currencySymbol = getCurrencySymbol(country);

  // ⭐ LIVE PRICING: Fetch live prices for investments
  const { prices: priceMap } = usePortfolioPrices(investments);

  // Load saved inputs on mount
  const [inputsLoaded, setInputsLoaded] = useState(false);

  // Input state
  const [inputs, setInputs] = useState({
    currentAge: 35,
    retirementAge: 65,
    lifeExpectancy: 95,
    desiredAnnualIncome: 60000,
    essentialSpending: 42000,
    discretionarySpending: 18000,
    currentAnnualIncome: 80000,
    inflationRate: 2.5,
    expectedReturn: 7.0,
    volatility: 15.0,
    riskProfile: 'moderate',
    withdrawalStrategy: 'inflationAdjusted',
    statePensionAge: country === 'UK' ? 66 : 67,
    includeStatePension: true,
    includeSpouseStatePension: true,
    oneOffExpenses: [],
    country: country,
    rentalProperties: [],
    partTimeWork: null,
    annuities: [],
    otherIncome: [],
    spouseIncome: null,
    ukTaxFreeLumpSumOptions: {
      takeAsLumpSum: true,
      phasedYears: 5
    }
  });

  // Load saved inputs on mount
  useEffect(() => {
    const savedInputs = loadRetirementInputs();
    if (savedInputs) {
      console.log('Loaded saved retirement inputs from localStorage');
      setInputs(prev => ({
        ...prev,
        ...savedInputs,
        country: country // Always use current country from props
      }));
    }
    setInputsLoaded(true);
  }, [country]);

  // Auto-save inputs when they change (debounced)
  useEffect(() => {
    if (inputsLoaded) {
      debouncedSaveInputs(inputs);
    }
  }, [inputs, inputsLoaded]);

  // Navigation state
  const [mainTab, setMainTab] = useState('configure');
  const [activeInputTab, setActiveInputTab] = useState('basics');
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('overview');

  // Calculation state
  const [calculating, setCalculating] = useState(false);
  const [results, setResults] = useState(null);
  const [monteCarloScenarios, setMonteCarloScenarios] = useState(1000);

  // Calculate state pension estimate
  const statePensionEstimate = useMemo(() => {
    if (!inputs.includeStatePension) return 0;

    return estimateStatePension({
      country: inputs.country,
      currentAge: inputs.currentAge,
      retirementAge: inputs.retirementAge,
      fullRetirementAge: inputs.statePensionAge,
      annualEarnings: inputs.currentAnnualIncome
    });
  }, [inputs.includeStatePension, inputs.country, inputs.currentAge, inputs.retirementAge, inputs.statePensionAge, inputs.currentAnnualIncome]);

  // Filter retirement accounts
  const retirementAccounts = useMemo(() => {
    return accounts.filter(account => {
      const retirementTypes = ['401k', 'roth_ira', 'traditional_ira', 'sep_ira', 'simple_ira',
                               'pension', 'sipp', 'isa', 'stocks_shares_isa', '403b', '457',
                               'thrift_savings_plan'];

      return retirementTypes.includes(account.type?.toLowerCase()) ||
             account.accountPurpose === 'retirement' ||
             account.accountPurpose === 'crypto' ||
             account.accountPurpose === 'taxable';
    });
  }, [accounts]);

  // Calculate total current retirement savings using LIVE market values
  const currentRetirementSavings = useMemo(() => {
    let total = 0;
    retirementAccounts.forEach(account => {
      const accountInvestments = investments.filter(inv => inv.accountId === account.id);
      // ⭐ LIVE PRICING: Use getInvestmentMarketValue with priceMap for accurate current values
      total += accountInvestments.reduce((sum, inv) => sum + getInvestmentMarketValue(inv, priceMap), 0);
    });
    return total;
  }, [retirementAccounts, investments, priceMap]);

  // Calculate total annual contributions
  const totalAnnualContributions = useMemo(() => {
    return retirementAccounts.reduce((sum, account) => sum + getAnnualContribution(account), 0);
  }, [retirementAccounts]);

  // Run calculations
  // autoSwitchTab: Only switch to analysis tab when user manually clicks "Recalculate"
  const runCalculations = async (autoSwitchTab = false) => {
    if (!isPremium) {
      alert('Retirement planning is a premium feature. Please upgrade your subscription.');
      return;
    }

    setCalculating(true);

    try {
      // 1. Accumulation phase
      // ⭐ LIVE PRICING: Pass priceMap for accurate starting balances
      const accumulationResult = simulateAccumulation({
        accounts: retirementAccounts,
        investments,
        currentAge: inputs.currentAge,
        retirementAge: inputs.retirementAge,
        expectedReturn: inputs.expectedReturn / 100,
        volatility: inputs.volatility / 100,
        inflationRate: inputs.inflationRate / 100,
        riskProfile: inputs.riskProfile,
        priceMap
      });

      // 2. Prepare accounts at retirement
      const accountsAtRetirement = {};
      const retirementProjection = accumulationResult.projections[accumulationResult.projections.length - 1];

      Object.keys(retirementProjection.accounts).forEach(accountId => {
        const originalAccount = accounts.find(acc => acc.id === accountId);
        accountsAtRetirement[accountId] = {
          balance: retirementProjection.accounts[accountId].balance,
          type: originalAccount?.type,
          name: originalAccount?.name
        };
      });

      // 3. Drawdown phase
      const drawdownResult = simulateDrawdown({
        accountsAtRetirement: accountsAtRetirement,
        currentAge: inputs.currentAge,
        retirementAge: inputs.retirementAge,
        lifeExpectancy: inputs.lifeExpectancy,
        desiredAnnualIncome: inputs.desiredAnnualIncome,
        essentialSpending: inputs.essentialSpending,
        discretionarySpending: inputs.discretionarySpending,
        withdrawalStrategy: inputs.withdrawalStrategy,
        inflationRate: inputs.inflationRate / 100,
        expectedReturn: inputs.expectedReturn / 100 * 0.8,
        volatility: inputs.volatility / 100,
        statePension: inputs.includeStatePension ? statePensionEstimate : 0,
        statePensionAge: inputs.statePensionAge,
        country: inputs.country,
        oneOffExpenses: inputs.oneOffExpenses,
        taxFreeLumpSumTaken: false,
        rentalProperties: inputs.rentalProperties,
        partTimeWork: inputs.partTimeWork,
        annuities: inputs.annuities,
        otherIncome: inputs.otherIncome,
        spouseIncome: inputs.spouseIncome,
        ukTaxFreeLumpSumOptions: inputs.ukTaxFreeLumpSumOptions,
        includeSpouseStatePension: inputs.includeSpouseStatePension
      });

      // 4. Calculate metrics
      const metrics = calculateRetirementMetrics({
        totalAtRetirement: accumulationResult.totalAtRetirement,
        desiredAnnualIncome: inputs.desiredAnnualIncome,
        currentAnnualIncome: inputs.currentAnnualIncome,
        statePension: statePensionEstimate,
        lifeExpectancy: inputs.lifeExpectancy,
        retirementAge: inputs.retirementAge
      });

      // 5. Monte Carlo simulation
      const monteCarloResult = runRetirementMonteCarlo({
        accountsAtRetirement: accountsAtRetirement,
        currentAge: inputs.currentAge,
        retirementAge: inputs.retirementAge,
        lifeExpectancy: inputs.lifeExpectancy,
        desiredAnnualIncome: inputs.desiredAnnualIncome,
        essentialSpending: inputs.essentialSpending,
        discretionarySpending: inputs.discretionarySpending,
        withdrawalStrategy: inputs.withdrawalStrategy,
        inflationRate: inputs.inflationRate / 100,
        expectedReturn: inputs.expectedReturn / 100 * 0.8,
        volatility: inputs.volatility / 100,
        statePension: inputs.includeStatePension ? statePensionEstimate : 0,
        statePensionAge: inputs.statePensionAge,
        country: inputs.country,
        numScenarios: monteCarloScenarios,
        oneOffExpenses: inputs.oneOffExpenses,
        rentalProperties: inputs.rentalProperties,
        partTimeWork: inputs.partTimeWork,
        annuities: inputs.annuities,
        otherIncome: inputs.otherIncome,
        spouseIncome: inputs.spouseIncome,
        includeSpouseStatePension: inputs.includeSpouseStatePension
      });

      // 6. Compare withdrawal strategies
      const withdrawalStrategyComparison = compareWithdrawalStrategies({
        accountsAtRetirement: accountsAtRetirement,
        currentAge: inputs.currentAge,
        retirementAge: inputs.retirementAge,
        lifeExpectancy: inputs.lifeExpectancy,
        desiredAnnualIncome: inputs.desiredAnnualIncome,
        essentialSpending: inputs.essentialSpending,
        discretionarySpending: inputs.discretionarySpending,
        inflationRate: inputs.inflationRate / 100,
        expectedReturn: inputs.expectedReturn / 100 * 0.8,
        volatility: inputs.volatility / 100,
        statePension: inputs.includeStatePension ? statePensionEstimate : 0,
        statePensionAge: inputs.statePensionAge,
        country: inputs.country,
        rentalProperties: inputs.rentalProperties,
        partTimeWork: inputs.partTimeWork,
        annuities: inputs.annuities,
        otherIncome: inputs.otherIncome,
        spouseIncome: inputs.spouseIncome,
        includeSpouseStatePension: inputs.includeSpouseStatePension
      });

      // 7. Generate insights
      const insights = generateRetirementInsights({
        metrics,
        accumulationResult,
        drawdownResult,
        monteCarloResult,
        withdrawalStrategyComparison,
        inputs: {
          ...inputs,
          statePension: statePensionEstimate
        }
      }, inputs.country);

      const actionItems = generateActionItems(insights);

      setResults({
        accumulationResult,
        drawdownResult,
        metrics,
        monteCarloResult,
        withdrawalStrategyComparison,
        insights,
        actionItems,
        statePensionEstimate,
        accountsAtRetirement
      });

      // Only auto-switch to analysis tab when user manually clicks "Recalculate"
      // Don't switch during auto-calculations from input changes
      if (autoSwitchTab) {
        setMainTab('analysis');
      }

    } catch (error) {
      console.error('Calculation error:', error);

      let errorMessage = 'Error running calculations. ';

      if (isNaN(inputs.currentAge) || inputs.currentAge <= 0) {
        errorMessage += 'Please enter a valid current age.';
      } else if (isNaN(inputs.retirementAge) || inputs.retirementAge <= inputs.currentAge) {
        errorMessage += 'Retirement age must be greater than current age.';
      } else if (isNaN(inputs.lifeExpectancy) || inputs.lifeExpectancy <= inputs.retirementAge) {
        errorMessage += 'Life expectancy must be greater than retirement age.';
      } else if (isNaN(inputs.desiredAnnualIncome) || inputs.desiredAnnualIncome <= 0) {
        errorMessage += 'Please enter a valid desired annual income.';
      } else {
        errorMessage += 'Please check all your inputs and try again.';
        errorMessage += '\n\nError details: ' + error.message;
      }

      alert(errorMessage);
    } finally {
      setCalculating(false);
    }
  };

  // Auto-calculate on mount and when key inputs change
  useEffect(() => {
    if (!inputsLoaded) return; // Don't run until inputs are loaded

    const hasValidInputs =
      retirementAccounts.length > 0 &&
      !isNaN(inputs.currentAge) && inputs.currentAge > 0 &&
      !isNaN(inputs.retirementAge) && inputs.retirementAge > inputs.currentAge &&
      !isNaN(inputs.lifeExpectancy) && inputs.lifeExpectancy > inputs.retirementAge &&
      !isNaN(inputs.desiredAnnualIncome) && inputs.desiredAnnualIncome > 0 &&
      !isNaN(inputs.currentAnnualIncome) && inputs.currentAnnualIncome >= 0;

    if (hasValidInputs) {
      runCalculations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputsLoaded,
    inputs.includeStatePension,
    inputs.includeSpouseStatePension,
    statePensionEstimate,
    inputs.rentalProperties,
    inputs.partTimeWork,
    inputs.annuities,
    inputs.otherIncome,
    inputs.spouseIncome,
    inputs.currentAge,
    inputs.retirementAge,
    inputs.lifeExpectancy,
    inputs.desiredAnnualIncome,
    inputs.essentialSpending,
    inputs.discretionarySpending,
    inputs.withdrawalStrategy,
    inputs.inflationRate,
    inputs.expectedReturn,
    inputs.volatility,
    inputs.statePensionAge,
    inputs.country,
    inputs.oneOffExpenses,
    inputs.ukTaxFreeLumpSumOptions?.takeAsLumpSum,
    inputs.ukTaxFreeLumpSumOptions?.phasedYears,
    retirementAccounts,
    investments,
    monteCarloScenarios
  ]);

  // Handle input changes
  const updateInput = (field, value) => {
    const numericFields = ['currentAge', 'retirementAge', 'lifeExpectancy',
      'desiredAnnualIncome', 'currentAnnualIncome', 'essentialSpending',
      'discretionarySpending', 'inflationRate', 'expectedReturn',
      'volatility', 'statePensionAge'];

    if (numericFields.includes(field)) {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return;
      }
      value = numValue;
    }

    setInputs(prev => ({ ...prev, [field]: value }));
  };

  // Handle spending split
  const updateSpendingSplit = (essentialPercent) => {
    const essential = (inputs.desiredAnnualIncome * essentialPercent) / 100;
    const discretionary = inputs.desiredAnnualIncome - essential;
    setInputs(prev => ({
      ...prev,
      essentialSpending: essential,
      discretionarySpending: discretionary
    }));
  };

  // Save current scenario
  const handleSaveScenario = () => {
    if (!results) {
      alert('Please run calculations before saving a scenario');
      return;
    }

    const scenarioName = prompt('Enter a name for this scenario:', `Scenario ${new Date().toLocaleDateString()}`);
    if (scenarioName) {
      const saved = saveScenario(scenarioName, inputs, results);
      if (saved) {
        alert(`Scenario "${scenarioName}" saved successfully!`);
      } else {
        alert('Error saving scenario. Please try again.');
      }
    }
  };

  // Load a saved scenario
  const handleLoadScenario = (scenarioInputs) => {
    if (window.confirm('Load this scenario? Your current inputs will be replaced.')) {
      setInputs(scenarioInputs);
      // Switch to analysis tab after loading scenario to see results
      setTimeout(() => runCalculations(true), 100);
    }
  };

  // Export results to JSON
  const exportResults = () => {
    if (!results) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      inputs,
      results: {
        metrics: results.metrics,
        accumulationSummary: {
          totalAtRetirement: results.accumulationResult.totalAtRetirement,
          totalContributions: results.accumulationResult.totalContributions,
          yearsToRetirement: results.accumulationResult.yearsToRetirement
        },
        drawdownSummary: {
          depletionAge: results.drawdownResult.depletionAge,
          success: results.drawdownResult.success,
          finalBalance: results.drawdownResult.finalBalance
        },
        monteCarlo: {
          successRate: results.monteCarloResult.successRate,
          medianDepletionAge: results.monteCarloResult.medianDepletionAge
        },
        insights: results.insights,
        actionItems: results.actionItems
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `retirement-plan-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle "Edit Inputs" button click from analysis
  const handleEditInputs = () => {
    setMainTab('configure');
  };

  if (!isPremium) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Premium Feature</AlertTitle>
          <AlertDescription>
            The Enhanced Retirement Insight is available for premium subscribers.
            Upgrade your plan to access comprehensive retirement planning with Monte Carlo simulations,
            tax optimization, and personalized insights.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const savedInfo = getSavedInputsInfo();

  return (
    <div className="pb-20"> {/* Add padding for floating action bar */}
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <Calculator className="h-6 w-6 sm:h-8 sm:w-8" />
              Retirement Insights
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
              Comprehensive retirement planning with Monte Carlo simulations
            </p>
            {savedInfo && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                💾 Last saved: {savedInfo.daysAgo === 0 ? 'today' : `${savedInfo.daysAgo} days ago`}
              </p>
            )}
          </div>
        </div>

        {/* Quick Stats - Always visible */}
        <QuickStatsPanel
          currentRetirementSavings={currentRetirementSavings}
          totalAnnualContributions={totalAnnualContributions}
          retirementAccounts={retirementAccounts}
          yearsToRetirement={inputs.retirementAge - inputs.currentAge}
          retirementAge={inputs.retirementAge}
          fundingRatio={results?.metrics?.fundingRatio}
          statePensionEstimate={statePensionEstimate}
          hasResults={!!results}
          currencySymbol={currencySymbol}
        />

        {/* Main Tabs: Configure | Analysis */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <TabsList className="inline-flex w-full min-w-max sm:grid sm:grid-cols-2 sm:min-w-0 mb-6">
              <TabsTrigger value="configure" className="text-sm sm:text-base whitespace-nowrap">
                <Settings2 className="h-4 w-4 mr-2" />
                Configure Inputs
              </TabsTrigger>
              <TabsTrigger value="analysis" className="text-sm sm:text-base whitespace-nowrap">
                <BarChart3 className="h-4 w-4 mr-2" />
                View Analysis
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Configure Tab */}
          <TabsContent value="configure" className="mt-0">
            <RetirementInputTabs
              inputs={inputs}
              updateInput={updateInput}
              updateSpendingSplit={updateSpendingSplit}
              setInputs={setInputs}
              country={country}
              currencySymbol={currencySymbol}
              statePensionEstimate={statePensionEstimate}
              accounts={accounts}
              investments={investments}
              monteCarloScenarios={monteCarloScenarios}
              setMonteCarloScenarios={setMonteCarloScenarios}
              activeInputTab={activeInputTab}
              setActiveInputTab={setActiveInputTab}
            />
          </TabsContent>

          {/* Analysis Tab */}
          <TabsContent value="analysis" className="mt-0">
            <AnalysisTabs
              results={results}
              inputs={inputs}
              retirementAccounts={retirementAccounts}
              currencySymbol={currencySymbol}
              statePensionEstimate={statePensionEstimate}
              activeAnalysisTab={activeAnalysisTab}
              setActiveAnalysisTab={setActiveAnalysisTab}
              onLoadScenario={handleLoadScenario}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Action Bar - Always visible */}
      <FloatingActionBar
        onSave={handleSaveScenario}
        onExport={exportResults}
        onRecalculate={() => runCalculations(true)}
        onEditInputs={handleEditInputs}
        calculating={calculating}
        hasResults={!!results}
        showEditButton={mainTab === 'analysis'}
      />
    </div>
  );
}
